import React from 'react';

import TabbedModal, {type TabType} from 'app/components/general/TabbedModal';
import {createFormErrorsContext, FormErrorsProps} from 'app/general/forms';
import {useUpdatePolicy} from 'app/policies/api';
import {useUpdateProcedure} from 'app/procedures/api';
import {useAccountSettingsState} from 'app/settings/api';
import {useInviteUser} from 'app/user/api';
import {gt} from 'app/utils/i18n';

import {SharePublicModalPane} from './SharePublicModalPane';
import {ShareQRCodeModalPane} from './ShareQRCodeModalPane';
import {ShareWithTeammateModalPane} from './ShareWithTeammateModalPane';

import type {BaseActionableDocumentType} from 'app/general/types';
import type {NewInvitationType, ShareableContentType} from 'app/user/types';

interface Props extends FormErrorsProps {
  hideTeammateTab?: boolean;
  hidePublicTab?: boolean;
  hideQRCodeTab?: boolean;
  show: boolean;
  contentType: ShareableContentType;
  onClose: () => void;
  initiallySelectedTab?: 'show-public' | 'show-qrcode' | 'show-team';
  object: BaseActionableDocumentType;
}

export const ShareDocumentModal = createFormErrorsContext<Props>(
  ({
    onClose,
    object,
    contentType,
    show,
    initiallySelectedTab,
    hideTeammateTab = false,
    hidePublicTab = false,
    hideQRCodeTab = false,
    setFormErrors,
  }: Props) => {
    const [updateProcedure] = useUpdateProcedure();
    const [updatePolicy] = useUpdatePolicy();
    const [inviteUser] = useInviteUser();

    const setPolicyPrivate = ({url, isPrivate}) =>
      updatePolicy({
        url,
        private: isPrivate,
        successMessage: isPrivate
          ? gt('This policy is now private')
          : gt('This policy is now public'),
      });

    const setProcedurePrivate = ({url, isPrivate}) =>
      updateProcedure({
        url,
        private: isPrivate,
        successMessage: isPrivate
          ? gt('This procedure is now private')
          : gt('This procedure is now public'),
      });

    const onChangePrivate = {
      procedure: setProcedurePrivate,
      proceduregroup: undefined,
      policy: setPolicyPrivate,
    }[contentType];

    const {STATIC_URL} = useAccountSettingsState().data!;

    function shareWithTeammate(invitations: Array<NewInvitationType>) {
      return inviteUser({invitations})
        .unwrap()
        .then(
          (response) => {
            setFormErrors({});
            onClose();
            return response;
          },
          (err) => {
            let formErrors = err.data[0];

            if (formErrors['to_user_id']) {
              formErrors.name = [gt('Please select existing teammembers')];
            }

            setFormErrors(formErrors);
          }
        );
    }

    const tabs: Array<TabType> = [];

    if (!hidePublicTab && onChangePrivate != null) {
      tabs.push({
        eventKey: 'show-public',
        name: gt('Share Publicly'),
        icon: 'fa fa-globe-americas',
        content: (
          <SharePublicModalPane
            object={object}
            onChange={onChangePrivate}
            onCancel={onClose}
            staticURL={STATIC_URL}
          />
        ),
      });
    }

    if (hideTeammateTab === false) {
      tabs.push({
        eventKey: 'show-team',
        name: gt('Share with Teammates'),
        icon: 'fa fa-users',
        content:
          object != null ? (
            <ShareWithTeammateModalPane
              objectId={
                object.content_type === 'document'
                  ? object.object_id
                  : object.id
              }
              name={object.name}
              contentType={contentType}
              onConfirm={shareWithTeammate}
              onCancel={onClose}
            />
          ) : null,
      });
    }

    if (!hideQRCodeTab) {
      tabs.push({
        eventKey: 'show-qrcode',
        name: gt('QR Code'),
        icon: 'fas fa-qrcode',
        content: <ShareQRCodeModalPane object={object} onCancel={onClose} />,
      });
    }

    return (
      <TabbedModal
        show={show}
        onCancel={onClose}
        tabs={tabs}
        initiallySelectedTab={initiallySelectedTab}
      />
    );
  }
);
